// Composio toolkit display helpers.
//
// The catalog/status endpoints are supposed to return clean toolkit slugs
// ("SLACK") and display names ("Slack"), but currently leak raw auth-config
// ids ("auth_config_asana_1783960412514"). These helpers recover a clean slug
// and human label from either shape, so the UI stays correct regardless of
// which the backend sends (defense-in-depth once the BE serializer is fixed).

const AUTH_CONFIG_RE = /^auth_config_(.+?)_\d+$/i;

/** Brands whose favicon domain isn't `<slug>.com`. Keys are lowercase slugs. */
const KNOWN_DOMAINS: Record<string, string> = {
  googledrive: "drive.google.com",
  google_drive: "drive.google.com",
  googlesheets: "docs.google.com",
  googledocs: "docs.google.com",
  googlecalendar: "calendar.google.com",
  gmail: "mail.google.com",
  jira: "atlassian.com",
  confluence: "atlassian.com",
  bitbucket: "bitbucket.org",
  linear: "linear.app",
  notion: "notion.so",
  msteams: "teams.microsoft.com",
  microsoft_teams: "teams.microsoft.com",
  outlook: "outlook.com",
  onedrive: "onedrive.live.com",
  twitter: "x.com",
  x: "x.com",
  posthog: "posthog.com",
  supabase: "supabase.com",
  vercel: "vercel.com",
  clickup: "clickup.com",
  hubspot: "hubspot.com",
  salesforce: "salesforce.com",
  zohobooks: "zoho.com",
  zohoanalytics: "zoho.com",
  whatsapp: "whatsapp.com",
  share_point: "sharepoint.com",
  google_maps: "maps.google.com",
  loops_so: "loops.so",
  mercury_mcp: "mercury.com",
  metaads: "facebook.com",
  googleads: "ads.google.com",
  adobe_illustrator: "adobe.com",
  illustrator_mcp: "adobe.com",
  adobe_photoshop: "adobe.com",
  photoshop_mcp: "adobe.com",
};

/** Acronyms/brand casings that plain title-case gets wrong. */
const SPECIAL_NAMES: Record<string, string> = {
  github: "GitHub",
  gitlab: "GitLab",
  googledrive: "Google Drive",
  google_drive: "Google Drive",
  googlesheets: "Google Sheets",
  googledocs: "Google Docs",
  googlecalendar: "Google Calendar",
  msteams: "Microsoft Teams",
  microsoft_teams: "Microsoft Teams",
  onedrive: "OneDrive",
  hubspot: "HubSpot",
  clickup: "ClickUp",
  posthog: "PostHog",
  whatsapp: "WhatsApp",
  x: "X (Twitter)",
  jira: "Jira",
  aws: "AWS",
  sap: "SAP",
  sap_successfactors: "SAP SuccessFactors",
  share_point: "SharePoint",
  google_maps: "Google Maps",
  loops_so: "Loops.so",
  mercury_mcp: "Mercury",
  metaads: "Meta Ads",
  googleads: "Google Ads",
  paypal: "PayPal",
  quickbooks: "QuickBooks",
  linkedin: "LinkedIn",
  netsuite: "NetSuite",
  canva: "Canva",
  figma: "Figma",
  adobe_illustrator: "Adobe Illustrator",
  illustrator_mcp: "Adobe Illustrator",
  adobe_photoshop: "Adobe Photoshop",
  photoshop_mcp: "Adobe Photoshop",
};

export interface ToolkitInfo {
  /** Clean lowercase slug, e.g. "asana" — safe for favicon-domain lookup. */
  slug: string;
  /** Human label, e.g. "Asana". */
  displayName: string;
}

function titleCase(slug: string): string {
  return slug
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Recover a clean slug + display name from a toolkit value that may be a
 * proper slug ("SLACK"), a display name ("Slack"), or a leaked auth-config id
 * ("auth_config_asana_1783960412514").
 */
export function parseToolkit(raw: string): ToolkitInfo {
  const m = AUTH_CONFIG_RE.exec(raw?.trim() ?? "");
  const slug = (m ? m[1] : (raw ?? "")).trim().toLowerCase();
  const displayName = SPECIAL_NAMES[slug] ?? titleCase(slug);
  return { slug, displayName };
}

/** Best-guess favicon domain for a toolkit slug (or raw toolkit value). */
export function toolkitDomain(slugOrRaw: string): string {
  const { slug } = parseToolkit(slugOrRaw);
  return KNOWN_DOMAINS[slug] ?? `${slug.replace(/_/g, "")}.com`;
}

/**
 * Product "families" shared between a first-party connector we built and its
 * Composio twin. We connect these products through our own OAuth/setup flow, so
 * the Composio twin is always hidden from the catalog — the first-party flow is
 * the single canonical way to connect the product.
 */
export type TwinFamily = "gmail" | "outlook" | "zohobooks" | "zohoanalytics" | "d365" | "d365bc";

/**
 * Map a Composio toolkit slug/value to a first-party twin family, or null.
 *
 * Composio only exposes one generic "Dynamics 365" toolkit (no distinct slug per
 * sub-product), so it stays mapped to "d365" and is hidden once D365 Finance is
 * connected. "d365bc" has no Composio twin to suppress.
 */
export function composioTwinFamily(rawToolkit: string): TwinFamily | null {
  const { slug } = parseToolkit(rawToolkit);
  if (slug === "gmail") return "gmail";
  if (slug === "outlook" || slug === "office365" || slug === "microsoftmail") return "outlook";
  if (slug === "zohobooks" || slug === "zohoinventory") return "zohobooks";
  if (slug === "zohoanalytics") return "zohoanalytics";
  if (slug.startsWith("dynamics365")) return "d365";
  return null;
}
